import { Chip, Divider, Text } from '@billda/design-system';
import { LabelValueRow } from '../LabelValueRow';
import { BreakdownRow } from '../BreakdownRow';
import { formatCurrency } from '../../../../shared/utils/currency';
import type { PaymentRowDto } from '../../api/paymentsApi';

interface PaymentDetailPanelContentProps {
  payment: PaymentRowDto;
  /** Individual pot rows when this payment is an aggregated worker row. */
  breakdownRows?: PaymentRowDto[];
}

export function PaymentDetailPanelContent({
  payment,
  breakdownRows,
}: PaymentDetailPanelContentProps) {
  const hasBreakdown = breakdownRows && breakdownRows.length > 1;

  return (
    <div className="flex flex-col gap-4 p-4">
      <LabelValueRow label="Person Name" value={payment.personName} />
      {!hasBreakdown && (
        <LabelValueRow label="Pot Reference" value={payment.potReference} />
      )}
      <LabelValueRow
        label="Status"
        value={
          <Chip
            label={payment.potStatus}
            size="sm"
            color={
              payment.potStatus === 'Paid' ||
              payment.potStatus === 'Partially Paid'
                ? 'success'
                : 'default'
            }
            variant={payment.potStatus === 'Paid' ? 'filled' : 'outlined'}
          />
        }
      />

      <Divider />

      <Text variant="body2" className="font-semibold">
        Bank Details
      </Text>
      <LabelValueRow
        label="Account Number"
        value={payment.accountNumber || '--'}
      />
      <LabelValueRow label="Sort Code" value={payment.sortCode || '--'} />
      <LabelValueRow label="Company Name" value={payment.companyName || '--'} />

      <Divider />

      <LabelValueRow
        label="Total Due"
        value={formatCurrency(payment.amountDue)}
        valueWeight="medium"
      />

      {hasBreakdown && (
        <>
          <Divider />
          <Text variant="body2" className="font-semibold">
            Breakdown
          </Text>
          <div data-testid="payment-breakdown" className="flex flex-col">
            {breakdownRows.map((row, i) => (
              <BreakdownRow
                key={`${row.potReference}-${i}`}
                row={row}
                data-testid={`payment-breakdown-row-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
